import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import isUndefined from 'lodash/isUndefined';
import { FiUpload } from 'react-icons/fi';
import { Typography, Button, Colors, AnalyticsProps } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';

const ContentWrapper = styled.div<AnalyticsProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${Colors.darkGray};
  border-radius: 10px;
  padding: 1rem;
`;

const InPlayerIconWrapper = styled(FiUpload)`
  display: block;
  text-align: center;
  font-size: ${({ theme }) => theme.font.sizes.extraLarge};
  color: ${Colors.gray};
  margin-bottom: 0.6rem;
`;

const Paragraph = styled(Typography)`
  text-align: center;
  font-size: ${({ theme }) => theme.font.sizes.small};
`;

const ButtonWrapper = styled(Button)`
  margin: 0.5rem 0;
`;

const SizeParagraph = styled(Paragraph)`
  font-size: 0.75rem;
`;

const BoldSpan = styled.span`
  font-weight: ${({ theme }) => theme.font.weights.normal};
  color: ${Colors.fontDarkGray};
`;

interface Props extends AnalyticsProps {
  resetForm?: () => any;
  uploadImage: (file: File, ...args: any[]) => any;
  disabled?: boolean;
}

const UploadFile = ({ uploadImage, disabled, resetForm, tag = 'button_uploadFile' }: Props) => {
  const [fileName, setFileName] = useState('');
  const onDrop = (files: Array<File>) => {
    if (resetForm) {
      resetForm();
    }
    const file = files[0];
    if (!isUndefined(file)) {
      uploadImage(file);
      setFileName(file.name);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, disabled });

  return (
    <ContentWrapper tag="box_uploadFile" {...getRootProps()}>
      <input {...getInputProps()} />
      <InPlayerIconWrapper />
      {!isDragActive ? (
        <>
          <Paragraph variant="p">
            <BoldSpan>Drag and drop </BoldSpan>files here
          </Paragraph>
          <Paragraph variant="p">or</Paragraph>
          <ButtonWrapper tag={tag} size="xs" type="button" disabled={disabled}>
            Select file
          </ButtonWrapper>
        </>
      ) : (
        <Paragraph variant="p">Drop the files here</Paragraph>
      )}
      <SizeParagraph variant="p">
        {fileName ? (
          `File to upload: ${fileName}`
        ) : (
          <>
            Maximum file size: <i>less than 1MB.</i>
          </>
        )}
      </SizeParagraph>
    </ContentWrapper>
  );
};

export default UploadFile;
